import { useEffect } from 'react';
import { Outlet, useMatch } from 'react-router-dom';
import BottomNavigation from './BottomNavigation';
import Sidebar from './Sidebar';
import ChatNotificationPopup from './ChatNotificationPopup';
import ServerStatusBanner from './ServerStatusBanner';
import useAuthStore from '../store/useAuthStore';
import useChatStore from '../store/useChatStore';
import { ensureValidSession } from '../utils/sessionAuth';
import './Layout.css';

/** Поля, при фокусе на которых реально открывается экранная клавиатура. */
function isKeyboardViewportElement(el) {
  if (!el) return false;
  if (el.tagName === 'TEXTAREA' || el.isContentEditable) return true;
  if (el.tagName !== 'INPUT') return false;
  const type = (el.getAttribute('type') || 'text').toLowerCase();
  return !['file', 'hidden', 'checkbox', 'radio', 'button', 'submit', 'reset', 'image', 'color', 'range'].includes(type);
}

/**
 * Высота видимой области для --app-visual-height и эвристики клавиатуры.
 * visualViewport иногда кратко отдаёт сильно заниженную высоту (инерция скролла,
 * адресная строка) — без фокуса в поле ввода не используем такие значения,
 * иначе ломается вёрстка (контент «на пол экрана»).
 */
function getCssViewportHeightPx() {
  const vv = window.visualViewport;
  const inner = window.innerHeight;
  if (!vv) return inner;
  const raw = Math.min(inner, Math.max(0, Math.round(vv.height + vv.offsetTop)));
  const typing = isKeyboardViewportElement(document.activeElement);
  if (typing) return raw;
  if (raw > 0 && raw < inner * 0.58) return inner;
  return raw;
}

function Layout() {
  const { user, token, isAuthenticated } = useAuthStore();
  const initSession = useChatStore((s) => s.initSession);
  const userId = user?.userId ?? user?.id;

  // Глобальная сессия чата: WS + фоновое обновление диалогов.
  // Откладываем до idle/таймаута — на iOS параллельный WS + 3 REST
  // конкурируют с загрузкой JS-бандла и auth verify.
  useEffect(() => {
    if (!isAuthenticated || !userId || !token) return undefined;

    let cancelled = false;
    const startChat = () => {
      if (!cancelled) initSession(userId, token);
    };

    let idleId;
    let timer;
    if (typeof requestIdleCallback === 'function') {
      idleId = requestIdleCallback(startChat, { timeout: 5000 });
    } else {
      timer = setTimeout(startChat, 3000);
    }

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      if (idleId != null && typeof cancelIdleCallback === 'function') {
        cancelIdleCallback(idleId);
      }
    };
  }, [isAuthenticated, userId, token, initSession]);

  useEffect(() => {
    if (!isAuthenticated) return;

    const checkSession = () => {
      if (document.hidden) return;
      ensureValidSession();
    };

    document.addEventListener('visibilitychange', checkSession);
    return () => document.removeEventListener('visibilitychange', checkSession);
  }, [isAuthenticated]);

  // Карточка ленты по URL (/feed/category/:key/:itemId) — без max-w-md для детального экрана.
  // Раньше опирались на location.state?.usr (в коде не задаётся и теряется при обновлении страницы).
  const isFeedDetail = Boolean(
    useMatch({ path: '/feed/category/:categoryKey/:itemId', end: true }),
  );

  // Высота видимой области (visualViewport) на мобильных: 100dvh/100vh часто
  // расходятся с реальным вьюпортом при показе/скрытии адресной строки Safari/Chrome
  // и до пересчёта после поворота — нижняя часть скролла оказывается под
  // position:fixed навбаром (z-index:20), тапы уходят в навбар или «в пустоту».
  // Поворот экрана форсирует resize и временно «чинит» симптом.
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;

    let resizeTimer = null;
    let lastSetHeight = 0;

    const apply = () => {
      const newHeight = getCssViewportHeightPx();
      // Защита от бесконечного цикла на iOS: если высота не изменилась — выходим.
      // Установка CSS custom property может вызвать re-layout → resize → сюда снова.
      if (newHeight === lastSetHeight) return;
      lastSetHeight = newHeight;
      document.documentElement.style.setProperty('--app-visual-height', `${newHeight}px`);
    };

    // Debounced-версия для resize: не чаще раза в 100 мс.
    const onResize = () => {
      if (resizeTimer) return;
      resizeTimer = setTimeout(() => {
        resizeTimer = null;
        apply();
      }, 100);
    };

    apply();

    vv.addEventListener('resize', onResize);
    vv.addEventListener('scroll', apply);
    // iOS: итоговая высота иногда приходит после анимации поворота
    const onOrientation = () => {
      requestAnimationFrame(apply);
      window.setTimeout(apply, 320);
    };
    window.addEventListener('orientationchange', onOrientation);
    const onVisibility = () => {
      if (!document.hidden) apply();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      if (resizeTimer) clearTimeout(resizeTimer);
      vv.removeEventListener('resize', onResize);
      vv.removeEventListener('scroll', apply);
      window.removeEventListener('orientationchange', onOrientation);
      document.removeEventListener('visibilitychange', onVisibility);
      document.documentElement.style.removeProperty('--app-visual-height');
    };
  }, []);

  // Глобальное определение виртуальной клавиатуры для всех страниц.
  // Когда клавиатура открыта — добавляем класс keyboard-open на <html>,
  // что скрывает нижнюю навбар и убирает отступы под неё.
  // Без этого нижняя навбар перекрывала кнопки форм (position:fixed z-index:20)
  // и перехватывала тапы, делая их некликабельными.
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;

    const THRESHOLD = 150; // px — минимальная разница высот, чтобы считать клавиатуру открытой

    // Используем отслеживаемый базис вместо window.screen.height:
    // screen.height на старых iOS не меняется при повороте устройства,
    // поэтому в ландшафтном режиме (vv.height ≈ 370) разница с
    // портретным screen.height (≈ 844) ложно превышает порог.
    let baselineHeight = getCssViewportHeightPx();

    const handleResize = () => {
      // Пропускаем, если сейчас открыт чат-разговор (там свой обработчик)
      if (document.documentElement.classList.contains('in-chat-conversation')) return;

      // Клавиатура физически не может появиться без фокуса на поле ввода.
      // Без этой проверки resize браузера на ПК (или скрытие/показ адресной
      // строки Safari) ложно взводит keyboard-open и прячет нижнюю навбар.
      const isInputFocused = isKeyboardViewportElement(document.activeElement);

      if (!isInputFocused) {
        // Без фокуса клавиатуры нет — обновляем базис (в т.ч. после поворота)
        baselineHeight = getCssViewportHeightPx();
        document.documentElement.classList.remove('keyboard-open');
        return;
      }

      const effectiveHeight = getCssViewportHeightPx();

      // Обновляем базис только вверх: если вьюпорт стал больше — клавиатура точно закрылась
      if (effectiveHeight > baselineHeight) {
        baselineHeight = effectiveHeight;
      }

      const isKbOpen = (baselineHeight - effectiveHeight) > THRESHOLD;
      document.documentElement.classList.toggle('keyboard-open', isKbOpen);
    };

    vv.addEventListener('resize', handleResize);
    return () => {
      vv.removeEventListener('resize', handleResize);
      // Снимаем класс только если чат его не добавил
      if (!document.documentElement.classList.contains('in-chat-conversation')) {
        document.documentElement.classList.remove('keyboard-open');
      }
    };
  }, []);

  // Fallback for mobile Safari/WebView: track focused form fields via DOM events.
  // Some iOS versions don't reliably support :has() or viewport keyboard signals.
  useEffect(() => {
    const applyFocusedClass = () => {
      const active = document.activeElement;
      document.documentElement.classList.toggle('form-input-focused', isKeyboardViewportElement(active));
    };

    const onFocusIn = () => applyFocusedClass();
    const onFocusOut = () => {
      // iOS may switch focus asynchronously while keyboard animates.
      requestAnimationFrame(applyFocusedClass);
      window.setTimeout(applyFocusedClass, 80);
    };

    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('focusout', onFocusOut);
    applyFocusedClass();

    return () => {
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('focusout', onFocusOut);
      document.documentElement.classList.remove('form-input-focused');
    };
  }, []);

  return (
    <div className="app-layout flex flex-row w-full bg-[#1a222c] overflow-hidden font-sans text-slate-900">
      <ServerStatusBanner />
      {/* Боковая панель — только на десктопе */}
      <Sidebar />

      {/* Основная область */}
      <div
        className={`flex-1 flex flex-col bg-slate-50 relative overflow-hidden${
          isFeedDetail ? '' : ' max-w-md md:max-w-none mx-auto md:mx-0'
        }${isFeedDetail ? '' : ' border-x md:border-x-0 shadow-2xl md:shadow-none'}`}
      >
        {/* Прокручиваемая область контента */}
        <div className="layout-content">
          <Outlet />
        </div>

        {/* Нижняя навигация — только на мобайле */}
        <BottomNavigation />

        {/* In-app push notifications (iPhone-style) */}
        <ChatNotificationPopup />
      </div>
    </div>
  );
}

export default Layout;
